import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { TelemetryService } from '../services/telemetry.service';

/**
 * Normalizes URL for comparison by removing trailing slashes.
 */
function normalizeUrl(url: string): string {
  return url?.replace(/\/+$/, '') || '';
}

/**
 * Checks if a URL belongs to our own API endpoints.
 */
function isOwnApi(url: string): boolean {
  const ownOrigins: string[] = [
    environment.apiOrigin,
    environment.authApiOrigin,
    environment.diagnosticApiOrigin,
    environment.decryptApiOrigin,
    environment.iothubBaseUrl,
    environment.deviceTypesUrl,
    environment.oasisApiOrigin,
    environment.realtimeOrigin,
    environment.defaultIngestionUrl,
    // Include all IoT Hub ingestion service URLs
    ...(environment.iotHubIngestionMap ? Object.values(environment.iotHubIngestionMap) : []),
  ].filter((origin): origin is string => !!origin && origin.length > 0);

  return ownOrigins.some(origin => url.startsWith(normalizeUrl(origin)));
}

/**
 * Adds OASIS-API-KEY and X-Correlation-ID headers to requests for authentication and tracing.
 * SECURITY: Only adds headers to requests targeting our own API origins.
 */
export const baseHeadersInterceptor: HttpInterceptorFn = (req, next) => {
  const telemetry = inject(TelemetryService);
  const url = req.url;
  const correlationHeaders = { 'X-Correlation-ID': telemetry.correlationId };

  // Check if this is a request to Oasis origin
  if (environment.oasisOrigin && url.startsWith(environment.oasisOrigin)) {
    const cloned = req.clone({
      setHeaders: { 'OASIS-API-KEY': environment.oasisApiKey, ...correlationHeaders },
    });
    return next(cloned);
  }

  // Check if this is a request to decrypt API (uses oasisApiKey)
  // MUST check BEFORE diagnostic service since decrypt URL is under diagnostic origin
  if (url.includes('/crypto/decrypt') || url.includes('/decrypt')) {
    const cloned = req.clone({
      setHeaders: { 'OASIS-API-KEY': environment.oasisApiKey, ...correlationHeaders },
    });
    return next(cloned);
  }

  // Check if this is a request to diagnostic service (uses oasisApiKey)
  if (environment.diagnosticApiOrigin &&
      url.startsWith(normalizeUrl(environment.diagnosticApiOrigin))) {
    const cloned = req.clone({
      setHeaders: { 'OASIS-API-KEY': environment.oasisApiKey, ...correlationHeaders },
    });
    return next(cloned);
  }

  // Check if this is our own API
  if (isOwnApi(url)) {
    const cloned = req.clone({
      setHeaders: { 'OASIS-API-KEY': environment.oasisApiKey, ...correlationHeaders },
    });
    return next(cloned);
  }

  // Third-party request - don't add any keys
  return next(req);
};
